import { computed, nextTick, watch, type ComputedRef, type Ref } from "vue";
import { useStore } from "vuex";
import { useAnnotationAttemptAdapter } from "@modules/annotation/composables/use-annotation-attempt-adapter.ts";
import { isEmpty } from "@utils/var.check";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant";

interface AnchoringAnnotation {
  anchor?: string;
}

interface UseSuggestedAnswerSectionSelectionOptions {
  sectionSelected: Ref<string>;
  suggestedAnswerAnchor: ComputedRef<string>;
  suggestedAnswerSectionKey: ComputedRef<string>;
}

export function useSuggestedAnswerSectionSelection(
  options: UseSuggestedAnswerSectionSelectionOptions,
): void {
  const store = useStore();
  const annotationAttemptAdapter = useAnnotationAttemptAdapter();

  const anchoringAnnotation = computed<AnchoringAnnotation | null>(() => {
    return store.getters[
      `${NAMESPACE.ANNOTATION}/getAnchoringAnnotation`
    ] as AnchoringAnnotation | null;
  });

  const forceSectionSelected = (selectedKey: string): void => {
    if (options.sectionSelected.value === selectedKey) {
      options.sectionSelected.value = "";
      nextTick(() => {
        options.sectionSelected.value = selectedKey;
      });
      return;
    }

    options.sectionSelected.value = selectedKey;
  };

  const updateSelectedSectionForAnchor = (targetAnchor: string): void => {
    if (isEmpty(targetAnchor)) {
      return;
    }

    if (
      !isEmpty(options.suggestedAnswerAnchor.value) &&
      targetAnchor.includes(options.suggestedAnswerAnchor.value)
    ) {
      forceSectionSelected(options.suggestedAnswerSectionKey.value);
    }
  };

  watch(
    [anchoringAnnotation, annotationAttemptAdapter.anchoringContextMessage],
    ([annotation, contextMessage]) => {
      const targetAnchor = contextMessage?.anchor?.referenceId ?? annotation?.anchor ?? "";
      updateSelectedSectionForAnchor(targetAnchor);
    },
    { immediate: true },
  );
}
